import type { ReactNode } from 'react'
import { DndContext, closestCenter, type DragEndEvent } from '@dnd-kit/core'
import { SortableContext, arrayMove, rectSortingStrategy, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { missingVariants, type Media } from '@nhanho/shared'

/**
 * Lưới ô vuông kéo–thả đổi thứ tự, **không biết gì về nội dung từng ô**.
 *
 * Tách ra 2026-09-23 khi dải "Cảm hứng" ở trang chủ cần đúng hành vi này nhưng ô lại là
 * "ảnh gốc + khung cắt" chứ không phải một `Media`. Chép thêm một bản `DndContext` thứ hai là
 * hai chỗ phải nhớ sửa mỗi lần đụng tới kéo–thả.
 *
 * `@dnd-kit` được chọn vì nhẹ, accessible (bàn phím), và không phụ thuộc DOM ref kiểu cũ.
 */
export function SortableGrid<T>({
  items,
  idOf,
  onChange,
  children,
  className = 'grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-5',
}: {
  items: T[]
  idOf: (item: T) => string
  onChange: (items: T[]) => void
  children: (item: T, index: number) => ReactNode
  className?: string
}) {
  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e
    if (!over || active.id === over.id) return
    const from = items.findIndex((i) => idOf(i) === active.id)
    const to = items.findIndex((i) => idOf(i) === over.id)
    onChange(arrayMove(items, from, to))
  }
  return (
    <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={items.map(idOf)} strategy={rectSortingStrategy}>
        <div className={className}>
          {items.map((item, i) => (
            <SortableTile key={idOf(item)} id={idOf(item)}>
              {children(item, i)}
            </SortableTile>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  )
}

/**
 * Một ô kéo được. Tay cầm là một `<button>` phủ kín ô, **nằm dưới** mọi nút nội dung đặt sau nó
 * trong DOM — nên nút "Bỏ ảnh"/"Sửa khung" vẫn bấm được bình thường.
 */
export function SortableTile({ id, children }: { id: string; children: ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`group relative aspect-square overflow-hidden rounded-image border border-border bg-sand ${isDragging ? 'z-10 shadow-float' : ''}`}
    >
      <button type="button" {...attributes} {...listeners} className="absolute inset-0 cursor-grab active:cursor-grabbing" aria-label="Kéo để đổi thứ tự" />
      {children}
    </div>
  )
}

/** Lưới ảnh sản phẩm: ảnh đầu = ảnh đại diện. */
export function SortableImages({ items, onChange, onRemove }: { items: Media[]; onChange: (items: Media[]) => void; onRemove: (id: string) => void }) {
  return (
    <SortableGrid items={items} idOf={(m) => m.id} onChange={onChange}>
      {(media, idx) => (
        <>
          {/*
            Ảnh đặt TRƯỚC tay cầm kéo trong DOM thì tay cầm nằm trên ảnh (đúng: kéo được ở mọi
            chỗ), nhưng `SortableTile` đã render tay cầm trước `children` — nên ảnh ở đây phải
            `pointer-events-none`, nếu không nó nuốt mất cú kéo.
          */}
          <img
            src={media.sizes?.thumbnail?.url ?? media.url}
            alt={media.alt}
            className="pointer-events-none absolute inset-0 size-full object-cover"
          />
          {idx === 0 &&<span className="absolute left-1.5 top-1.5 rounded-full bg-primary-700 px-2 py-0.5 text-2xs font-medium text-white">Đại diện</span>}
          <button
            type="button"
            onClick={() => onRemove(media.id)}
            aria-label="Bỏ ảnh"
            className="absolute right-1.5 top-1.5 hidden size-6 place-items-center rounded-full bg-white/90 text-xs text-danger shadow group-hover:grid"
          >
            ✕
          </button>
          {/*
            Hai nhãn xếp chồng trong MỘT khối neo đáy, không phải hai `absolute bottom-0` rời
            nhau — rời nhau là chúng đè lên nhau đúng lúc ảnh vừa thiếu alt vừa nhỏ.
            Số đo là của ảnh GỐC: đó mới là con số quyết định api sinh được biến thể nào.
          */}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 flex flex-col">
            {!media.alt.trim() && <span className="bg-danger/80 px-1.5 py-0.5 text-xs text-white">Thiếu alt</span>}
            <span className={`px-1.5 py-0.5 text-xs text-white ${missingVariants(media.width, media.height).length > 0 ? 'bg-warning/85' : 'bg-ink/65'}`}>
              {media.width}×{media.height}
            </span>
          </div>
        </>
      )}
    </SortableGrid>
  )
}
